if (!customElements.get('video-component')) {
  class VideoComponent extends HTMLElement {
    constructor() {
      super();

      this.loaded = false;

      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.loaded) this.#lazyLoadVideo();
        }
      });

      this.observer.observe(this);
    }

    #lazyLoadVideo() {
      const html = JSON.parse(this.getAttribute('data-video'));

      this.querySelector('video').outerHTML = html;
      this.elVideo = this.querySelector('video');
      this.elVideo.classList.add('in-view');

      this.#attachCaptionsTrack();
      this.#bind();

      if (!this.#respectReducedMotion()) {
        this.#tryAutoplay();
      }

      this.loaded = true;
    }

    #attachCaptionsTrack() {
      const url = this.dataset.captionsUrl;
      if (!url || !this.elVideo) return;

      const elTrack = document.createElement('track');
      elTrack.kind = 'captions';
      elTrack.src = url;
      elTrack.srclang = this.dataset.captionsLang || 'en';
      elTrack.label = this.dataset.captionsLabel || 'English';
      elTrack.default = true;
      this.elVideo.appendChild(elTrack);
    }

    #respectReducedMotion() {
      if (this.getAttribute('autoplay') !== 'true') return false;
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

      this.elVideo.pause();
      this.elVideo.removeAttribute('autoplay');
      this.elVideo.autoplay = false;
      this.#syncPlayState();

      return true;
    }

    #tryAutoplay() {
      if (this.getAttribute('autoplay') !== 'true') return;

      this.elVideo.muted = true;

      const playPromise = this.elVideo.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => this.#syncPlayState())
          .catch(() => this.#syncPlayState());
      }
    }

    #bind() {
      this.elPlay = this.querySelector('[data-play]');
      this.elPlayLabel = this.querySelector('[data-play-label]');
      this.elProgress = this.querySelector('progress');

      this.#syncPlayState();

      this.elVideo.addEventListener('play', () => this.#syncPlayState());
      this.elVideo.addEventListener('pause', () => this.#syncPlayState());

      this.elPlay && this.elPlay.addEventListener('click', () => this.#togglePlay());
      this.elProgress && this.elVideo.addEventListener('timeupdate', () => this.#trackProgress());

      if (this.getAttribute('autoplay') === 'false' && this.getAttribute('controls') === 'false') {
        this.addEventListener('click', (e) => {
          if (e.target.closest('[data-play]')) return;
          if (!this.elVideo.paused) this.elVideo.pause();
        });

        this.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          if (!this.elVideo.paused) {
            e.preventDefault();
            this.elVideo.pause();
          }
        });
      }
    }

    #togglePlay() {
      this.elVideo.paused ? this.elVideo.play() : this.elVideo.pause();
    }

    #syncPlayState() {
      if (!this.elVideo) return;

      const paused = this.elVideo.paused;
      this.setAttribute('data-playing', String(!paused));

      if (this.elPlay) {
        this.elPlay.hidden = !paused;

        if (this.elPlayLabel) {
          this.elPlayLabel.textContent = paused
            ? this.dataset.i18nPlay
            : this.dataset.i18nPause;
        }
      }

      const clickToPause = this.getAttribute('autoplay') === 'false'
        && this.getAttribute('controls') === 'false';

      if (clickToPause) {
        if (paused) {
          this.removeAttribute('tabindex');
          this.removeAttribute('aria-label');
          this.removeAttribute('role');
        } else {
          this.setAttribute('tabindex', '0');
          this.setAttribute('role', 'button');
          this.setAttribute('aria-label', this.dataset.i18nPause);
        }
      }
    }

    #trackProgress() {
      if (!this.elProgress) return;

      const { duration, currentTime } = this.elVideo;
      if (duration) this.elProgress.value = Math.round((currentTime / duration) * 100);
    }
  }

  customElements.define('video-component', VideoComponent);
}
